/**
 * CSV parsing and recurring-payment detection.
 *
 * Deliberately dependency-free: bank exports are small enough that a parser is
 * cheaper than a library, and the failure modes are ones we can see.
 */

/**
 * RFC4180-ish parser. Handles quoted fields, embedded commas and newlines, and
 * doubled quotes. Bank exports routinely contain all three in the description.
 */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const src = text.replace(/^﻿/, ""); // strip BOM — Excel exports carry one

  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
      continue;
    }
    if (c === '"') inQuotes = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((v) => v !== "")) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((v) => v !== "")) rows.push(row);

  if (!rows.length) return { headers: [], rows: [] };
  const headers = rows[0].map((h) => h.trim());
  return {
    headers,
    rows: rows.slice(1).map((r) => Object.fromEntries(headers.map((h, i) => [h, (r[i] ?? "").trim()]))),
  };
}

/** Money as banks write it: "1,234.56", "£1,234.56", "(123.45)", "-1,234.56". */
export function parseAmount(v) {
  if (v == null || v === "") return null;
  const s = String(v).trim();
  const negative = /^\(.*\)$/.test(s) || s.startsWith("-");
  const n = Number(s.replace(/[()]/g, "").replace(/[£$€,\s]/g, "").replace(/^-/, ""));
  if (isNaN(n)) return null;
  return negative ? -n : n;
}

/**
 * Dates as banks write them. UK exports are overwhelmingly day-first, so an
 * ambiguous 01/02/2026 is read as 1 February — guessing month-first would
 * silently shift a third of every statement.
 */
export function parseCsvDate(v) {
  if (!v) return null;
  const s = String(v).trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/.exec(s);
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) y = `20${y}`;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (!isNaN(d)) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  }
  return null;
}

/** Guess which columns are which, so the mapping starts mostly right. */
export function guessColumnMap(headers) {
  const find = (...patterns) =>
    headers.find((h) => patterns.some((p) => new RegExp(p, "i").test(h))) || "";
  return {
    date: find("^date$", "transaction.?date", "^posted", "value.?date", "date"),
    description: find("description", "narrative", "details", "reference", "payee", "merchant"),
    amount: find("^amount$", "value", "^paid", "debit|credit", "money"),
    paidOut: find("paid.?out", "^debit", "withdraw"),
    paidIn: find("paid.?in", "^credit", "deposit"),
    balance: find("balance"),
  };
}

/**
 * Normalise a payee so the same supplier groups together across statement
 * lines. Card references, dates and trailing digits vary per transaction and
 * would otherwise make every payment look unique.
 */
export function normalisePayee(description) {
  return (description || "")
    .toUpperCase()
    .replace(/\b\d{2}[/-]\d{2}([/-]\d{2,4})?\b/g, " ")   // embedded dates
    .replace(/\bC[DR]\s*\d+\b/g, " ")                     // card refs: "CD 1234"
    .replace(/\b\d{6,}\b/g, " ")                          // long reference numbers
    .replace(/\b(BGC|BAC|DD|SO|FPI|FPO|TFR|CHG|POS|ATM|VIS|DEB|CRD)\b/g, " ")
    .replace(/[^A-Z0-9&' ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const MONTH_MS = 86400000;
const median = (xs) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};

/**
 * Find repeating payments in imported rows.
 *
 * Groups by normalised payee, then judges cadence from the median gap between
 * occurrences. Requires at least three, because two payments are a coincidence.
 * Everything it returns is a proposal for a human to confirm — nothing here
 * writes to the forecast on its own.
 */
export function detectRecurring(transactions, { today = new Date() } = {}) {
  const groups = {};
  for (const t of transactions) {
    if (!t.txn_date || t.amount == null || t.amount >= 0) continue; // costs only
    const key = normalisePayee(t.description);
    if (!key || key.length < 3) continue;
    (groups[key] = groups[key] || []).push(t);
  }

  const out = [];
  for (const [payee, rows] of Object.entries(groups)) {
    if (rows.length < 3) continue;
    rows.sort((a, b) => new Date(a.txn_date) - new Date(b.txn_date));
    const gaps = [];
    for (let i = 1; i < rows.length; i++) {
      gaps.push((new Date(rows[i].txn_date) - new Date(rows[i - 1].txn_date)) / MONTH_MS);
    }
    const gap = median(gaps);
    // Wide bands: real statements drift by days around weekends and month ends.
    const frequency =
      gap <= 10 ? "weekly" : gap <= 45 ? "monthly" : gap <= 135 ? "quarterly" : gap <= 400 ? "annual" : null;
    if (!frequency) continue;

    const amounts = rows.map((r) => Math.abs(Number(r.amount)));
    const typical = median(amounts);
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    const isVariable = typical > 0 && (max - min) / typical > 0.2;

    const last = new Date(rows[rows.length - 1].txn_date);
    const daysSinceLast = Math.round((today - last) / MONTH_MS);
    const expectedGap = gap * 1.8 + 20;

    const dom = rows.map((r) => new Date(r.txn_date).getDate());

    out.push({
      payee,
      exampleDescription: rows[rows.length - 1].description,
      occurrences: rows.length,
      frequency,
      typicalAmount: Math.round(typical * 100) / 100,
      minAmount: min,
      maxAmount: max,
      isVariable,
      dayOfMonth: Math.round(median(dom)),
      firstSeen: rows[0].txn_date,
      lastSeen: rows[rows.length - 1].txn_date,
      // "Stopped" rather than "inactive": a regular payment that has missed
      // nearly two cycles is worth a look either way.
      stopped: daysSinceLast > expectedGap,
      annualValue: Math.round(
        typical * (frequency === "weekly" ? 52 : frequency === "monthly" ? 12 : frequency === "quarterly" ? 4 : 1)
      ),
    });
  }

  return out.sort((a, b) => b.annualValue - a.annualValue);
}
